import {DOM} from "../../../../../public/js/utils/DOM";
import {SalesPriceDataValue} from "../../../Sales/definitions/SalesPriceDataValue";
import {Price} from "../../../../../public/js/utils/Price";

export namespace ControlsFormMultiChooseInput {

    export class Config {
        public static readonly inputContainerClass = "MultiChooseInput";
        public static readonly inputSelectClass = "MultiChooseInput-select";
        public static readonly inputOptionItemClass = "MultiChooseInput-choose-option";
        public static readonly optionPriceDataName = "price";
        public static readonly optionExtraPriceDataName = "extraPrice";
        public static readonly optionExtraPriceCurrentDataName = "extraPriceCurrent";
        public static readonly skipPriceInitDataName = "skipPriceInit";
        public static readonly optionPriceTargetElementClass = "MultiChooseInput-choose-option-right-price";
        public static readonly optionIdentifyIdDataName = "identify";
        public static readonly changeModeDataName = "changeMode";
        public static readonly multiDataName = "multi";
        public static readonly enabledDataName = "enabled";
        public static readonly currentDataName = "current";
        public static readonly disabledDataName = "disabled";
        public static readonly alwaysEnabledDataName = "always";
    }

    export class Control {

        /**
         * Main initialization
         * @param container - form container
         */
        public static initialize(container?: HTMLElement) : void {
            const inputs = DOM.Parser.getHtmlElements(Config.inputContainerClass, container, true) as HTMLCollectionOf<HTMLDivElement>;
            for (let i = 0; i < inputs.length; i++) {
                this.initializeElement(inputs[i]);
            }
        }

        /**
         * Run input initialization
         * @param element - concrete input element
         * @private
         */
        private static initializeElement(element: HTMLDivElement) : void {

            // Update size of container
            DOM.Parser.getParentElement(element).classList.replace('m6', 'm12');
            DOM.Parser.getParentElement(element).classList.replace('l4', 'l12');

            // Process another initializations
            this.initializePrices(element);
            this.triggerOptionToggles(element);
            this.syncWithSelectInput(element);
        }

        /**
         * Show prices using JS formatter
         * Total price is required to be merged with normal (wtf)
         * @param element - main input container
         * @param multiplyExtraPrice - multiply price value (used in VPS to multiply licence price by CPU)
         * @see SalesVpsUtil
         * @private
         */
        private static initializePrices(element: HTMLDivElement, multiplyExtraPrice?: number) : void {

            const skipPriceInit = DOM.Parser.getDataByNameFromElementOrNull(Config.skipPriceInitDataName, element) === "yes";
            if (skipPriceInit) { return; }
            this.updatePrices(element, multiplyExtraPrice);
        }

        /**
         * Update prices using JS formatter
         * @param element - main input container
         * @param multiplyExtraPrice - multiply price value (used in VPS to multiply licence price by CPU)
         * @private
         */
        public static updatePrices(element: HTMLDivElement, multiplyExtraPrice?: number) : void {

            // Go through all options
            const changeMode = DOM.Parser.getDataByNameFromElementOrNull(Config.changeModeDataName, element) === "yes";
            const options = DOM.Parser.getHtmlElements(Config.inputOptionItemClass, element, true);

            for (let i = 0; i < options.length; i++) {

                // Get price object (for now always present)
                const option = options[i];
                const priceData = DOM.Parser.getDataByNameFromElementOrNull(Config.optionPriceDataName, option);
                const extraPriceData = DOM.Parser.getDataByNameFromElementOrNull(Config.optionExtraPriceDataName, option);
                const extraPriceCurrentData = DOM.Parser.getDataByNameFromElementOrNull(Config.optionExtraPriceCurrentDataName, option);
                const price : SalesPriceDataValue|null = priceData === null ? null : JSON.parse(priceData) as SalesPriceDataValue;
                const extraPrice : SalesPriceDataValue|null = extraPriceData === null ? null : JSON.parse(extraPriceData) as SalesPriceDataValue;
                const extraPriceCurrent : SalesPriceDataValue|null = extraPriceCurrentData === null ? null : JSON.parse(extraPriceCurrentData) as SalesPriceDataValue;

                // Merge prices
                if (price === null && extraPrice === null) { continue; }

                const totalPrice: SalesPriceDataValue = {
                    // Price + ( extra price * multiply ) - current
                    // Current have multiply already in price
                    price: ((price?.price ?? 0) + ((extraPrice?.price ?? 0) * (multiplyExtraPrice ? multiplyExtraPrice : 1))) - (extraPriceCurrent ? extraPriceCurrent.price : 0),
                    currency: price ? price.currency : extraPrice!.currency
                };

                // Update current price multiply if changed
                if (extraPrice) {
                    extraPrice.multiply = multiplyExtraPrice;
                    option.dataset[Config.optionExtraPriceDataName] = JSON.stringify(extraPrice);
                }

                // Show price in target element
                const targetPriceElement = DOM.Parser.getHtmlElementByClass(Config.optionPriceTargetElementClass, option);
                targetPriceElement.innerText = (new Price.Initializer()).getPriceAmountStringFromPriceObject(totalPrice, false, changeMode);
            }
        }

        /**
         * Init toggle enable / disable actions
         * @param element - main input container
         * @private
         */
        private static triggerOptionToggles(element: HTMLDivElement) : void {

            // Go through all option triggers
            const isMulti : boolean = DOM.Parser.getDataByNameFromElement(Config.multiDataName, element) === 'yes';
            const triggers = DOM.Parser.getHtmlElements(Config.inputOptionItemClass, element, true);
            for (let i = 0; i < triggers.length; i++) {

                // On click init
                const trigger = triggers[i];
                trigger.addEventListener('click', () => {

                    // If option is disabled stop
                    const optionDisabled = DOM.Parser.getDataByNameFromElementOrNull(Config.disabledDataName, trigger) === "yes";
                    const optionAlwaysEnabled = DOM.Parser.getDataByNameFromElementOrNull(Config.alwaysEnabledDataName, trigger) === "yes";
                    if (optionDisabled || optionAlwaysEnabled) { return; }

                    // Get main option info
                    trigger.dataset.enabled = trigger.dataset.enabled === 'yes' ? 'no' : 'yes';
                    const clickedIdentify = DOM.Parser.getDataByNameFromElement(Config.optionIdentifyIdDataName, trigger);

                    // If not multi remove all other boxes
                    if (!isMulti) {
                        for (let j = 0; j < triggers.length; j++) {
                            const boxOption = triggers[j];
                            const boxOptionIdentify = DOM.Parser.getDataByNameFromElement(Config.optionIdentifyIdDataName, boxOption);
                            if (boxOptionIdentify !== clickedIdentify) {
                                boxOption.dataset.enabled = 'no';
                            }
                        }
                    }

                    this.syncWithSelectInput(element);
                })
            }
        }

        /**
         * Update original select with changes
         * @param element - multi choose element
         * @private
         */
        private static syncWithSelectInput(element: HTMLDivElement) : void {

            // Get boxes and enabled options
            const enabledIds = [];
            const optionBoxes = DOM.Parser.getHtmlElements(Config.inputOptionItemClass, element, true);
            for (let i = 0; i < optionBoxes.length; i++) {
                const optionBox = optionBoxes[i];
                const enabled = DOM.Parser.getDataByNameFromElement(Config.enabledDataName, optionBox) === 'yes';
                if (!enabled) { continue; }
                const identify = DOM.Parser.getDataByNameFromElement(Config.optionIdentifyIdDataName, optionBox);
                enabledIds.push(identify);
            }

            // Get all select options and sync from boxes
            const selectInput = DOM.Parser.getHtmlElementByClass(Config.inputSelectClass, element);
            const options = DOM.Parser.getHtmlElementsByQuery('option', selectInput) as NodeListOf<HTMLOptionElement>;
            for (let j = 0; j < options.length; j++) {
                const option = options[j];
                if (enabledIds.includes(option.value)) {
                    option.setAttribute('selected', 'selected');
                } else {
                    option.removeAttribute('selected');
                }
            }

            selectInput.dispatchEvent(new Event("change"));
        }

        /**
         * Get price list from input
         * @param multiChoose - multi choose
         * @param changeMode - change mode enabled
         */
        public static getMultiChoosePriceList(multiChoose: HTMLElement, changeMode: boolean) : SalesPriceDataValue[] {

            // Process through options, find only enabled with price filled
            const options = DOM.Parser.getHtmlElements(Config.inputOptionItemClass, multiChoose);
            const prices: SalesPriceDataValue[] = [];

            // Process through options, find only enabled with price filled
            for (let j = 0; j < options.length; j++) {

                // Trigger change event to re-calculate
                const option = options[j];

                // Get state of current option
                const enabled = DOM.Parser.getDataByNameFromElementOrNull(Config.enabledDataName, option) === "yes";
                const current = DOM.Parser.getDataByNameFromElementOrNull(Config.currentDataName, option) === "yes";
                const price = DOM.Parser.getDataByNameFromElementOrNull(Config.optionPriceDataName, option);
                const extraPrice = DOM.Parser.getDataByNameFromElementOrNull(Config.optionExtraPriceDataName, option);

                // Get price using the state
                let priceDecoded = price ? JSON.parse(price) as SalesPriceDataValue : null;
                let extraPriceDecoded = extraPrice ? JSON.parse(extraPrice) as SalesPriceDataValue : null;

                if (changeMode) {
                    if (priceDecoded && ((!current || !enabled) && (current || enabled))) {
                        priceDecoded.price = (current && !enabled) ? -priceDecoded.price : priceDecoded.price
                        prices.push(priceDecoded);
                    }
                    if (extraPriceDecoded) {

                        const extraPriceCurrent = DOM.Parser.getDataByNameFromElementOrNull(Config.optionExtraPriceCurrentDataName, option);
                        let extraPriceCurrentDecoded = extraPriceCurrent ? JSON.parse(extraPriceCurrent) as SalesPriceDataValue : null;

                        // ( Extra price * multiply ) - current
                        // Current val have multiply already in price
                        const price = (extraPriceDecoded.price * (extraPriceDecoded.multiply ? extraPriceDecoded.multiply : 1))
                            - (extraPriceCurrentDecoded?.price ? extraPriceCurrentDecoded.price : 0)

                        // Decide if added or removed
                        prices.push({price: price, currency: extraPriceDecoded.currency});
                    }
                } else {
                    if (!enabled) { continue; }
                    if (priceDecoded) { prices.push(priceDecoded); }
                    if (extraPriceDecoded) { prices.push(extraPriceDecoded); }
                }
            }

            return prices;
        }
    }
}